import { Switch as HeadlessSwitch } from '@headlessui/react'
import { cn } from '@sparkai/utils'
import { ComponentPropsWithoutRef, forwardRef } from 'react'

export interface SwitchProps extends Omit<ComponentPropsWithoutRef<'button'>, 'onChange'> {
  checked?: boolean
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  /** For accessibility */
  description?: string
  name?: string
  /** For usage with name */
  value?: string
  labelClassName?: string
  groupClassName?: string
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onChange, description, className, labelClassName, groupClassName, disabled, ...props },
  ref
) {
  return (
    <HeadlessSwitch.Group>
      <div className={cn('flex items-center gap-2', groupClassName)}>
        <HeadlessSwitch
          ref={ref}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={cn(
            checked
              ? 'bg-brand-blue-500 disabled:bg-brand-blue-300'
              : 'bg-brand-gray-300 disabled:to-brand-gray-200',
            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent',
            'transition-colors duration-200 ease-in-out',
            'focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed',
            className
          )}
          {...props}
        >
          <span
            aria-hidden='true'
            className={cn(
              checked ? 'translate-x-5' : 'translate-x-0',
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0',
              'transition duration-200 ease-in-out'
            )}
          />
        </HeadlessSwitch>
        <HeadlessSwitch.Label
          className={cn(
            'mr-4 w-max text-xs font-medium ',
            {
              'text-brand-gray-400': disabled,
              'text-brand-gray-500': !disabled,
            },
            labelClassName
          )}
        >
          {description}
        </HeadlessSwitch.Label>
      </div>
    </HeadlessSwitch.Group>
  )
})
